import Seo from "@/components/Seo";
import { offerSchema } from "@/lib/seo";
import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check, MoveRight, Quote } from "lucide-react";
import { Reveal, RevealLine } from "@/components/Reveal";
import { HERO, HOME } from "@/constants/testIds";

const HERO_IMG = "https://images.unsplash.com/photo-1631679706909-1844bbd07221?auto=format&fit=crop&w=1400&q=80";
const HERO_IMG_2 = "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=900&q=80";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <section ref={ref} data-testid={HERO.section} className="lak-section relative min-h-[100svh] pt-32 sm:pt-40 pb-20 overflow-hidden">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-6 lg:gap-10 items-end">
        <motion.div style={{ y: useTransform(scrollYProgress, [0, 1], [0, -40]) }} className="col-span-12 lg:col-span-7 relative z-10">
          <Reveal>
            <span className="eyebrow">Boutique Web Design For Modern Brands</span>
          </Reveal>
          <h1 aria-label="Websites Designed To Feel Like Your Brand." className="mt-8 font-serif text-[44px] sm:text-[68px] lg:text-[96px] leading-[0.96] tracking-[-0.02em] font-light">
            <span className="block overflow-hidden">
              <motion.span initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1], delay: 0.05 }} className="block">Websites Designed</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1], delay: 0.15 }} className="block">To Feel Like</motion.span>
            </span>
            <span className="block overflow-hidden italic text-lak-sage">
              <motion.span initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1], delay: 0.25 }} className="block">Your Brand.</motion.span>
            </span>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-10 max-w-xl text-lg sm:text-xl text-lak-soft font-light leading-relaxed">
              Beautifully tailored websites for businesses tired of looking like everyone else.
              Custom design. Considered, every detail. Made only for your brand.
            </p>
          </Reveal>
          <Reveal delay={0.7}>
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <Link to="/contact" data-testid={HERO.primaryCta} className="inline-flex items-center gap-3 px-7 sm:px-9 py-4 sm:py-5 btn-sage text-[11px] tracking-[0.28em] uppercase">
                Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
              </Link>
              <Link to="/work" data-testid={HERO.secondaryCta} className="inline-flex items-center gap-3 px-7 sm:px-9 py-4 sm:py-5 btn-outline text-[11px] tracking-[0.28em] uppercase">
                View Our Work <MoveRight size={16} strokeWidth={1.25} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.9}>
            <div className="mt-16 flex items-center gap-6">
              <div className="vrule h-12" />
              <div>
                <p className="font-serif text-2xl italic text-lak-text">Your website, free <span className="not-italic font-light text-lak-soft text-base">·</span> £99/month</p>
                <p className="eyebrow mt-1">A considered new standard in web design</p>
              </div>
            </div>
          </Reveal>
        </motion.div>

        <motion.div style={{ y, opacity }} className="col-span-12 lg:col-span-5 relative">
          <Reveal delay={0.3}>
            <div className="relative aspect-[4/5] w-full overflow-hidden">
              <img src={HERO_IMG} alt="Atelier interior" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-lak-text/5"></div>
            </div>
          </Reveal>
          <Reveal delay={0.6}>
            <div className="hidden lg:block absolute -bottom-10 -left-16 w-52 aspect-[3/4] overflow-hidden border-8 border-lak-bg">
              <img src={HERO_IMG_2} alt="Detail" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </motion.div>
      </div>

      <div className="absolute bottom-8 inset-x-0 max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 flex items-center justify-between text-xs text-lak-soft">
        <span className="eyebrow">Est. London · United Kingdom</span>
        <span className="eyebrow hidden sm:inline">Scroll to explore ↓</span>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [
    "Custom Design", "Mobile First", "Hosting Included", "Lead Capture",
    "SEO Foundations", "Ongoing Support", "Luxury Aesthetic", "Strategic Build",
  ];
  return (
    <section className="lak-section border-y border-black/10 bg-lak-bg overflow-hidden">
      <div className="flex items-center marquee-track py-6 whitespace-nowrap">
        {[...items, ...items, ...items].map((label, i) => (
          <span key={i} className="font-serif italic text-3xl sm:text-4xl text-lak-soft mx-10 flex items-center gap-10">
            {label}
            <span className="w-2 h-2 bg-lak-highlight rounded-full inline-block" />
          </span>
        ))}
      </div>
    </section>
  );
}

function Problem() {
  const items = [
    { n: "01", title: "Generic Templates", body: "Most businesses look identical online. Same colours. Same layouts. Same forgettable feeling." },
    { n: "02", title: "Outdated Websites", body: "Customers judge your credibility within seconds. A tired site costs trust before a word is read." },
    { n: "03", title: "No Website At All", body: "Potential customers can't trust what they can't find. Visibility is the foundation of growth." },
  ];
  return (
    <section data-testid={HOME.problemSection} className="lak-section py-28 sm:py-40 bg-lak-bg">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-10 items-end mb-20">
          <div className="col-span-12 lg:col-span-7">
            <Reveal><span className="eyebrow">The first impression</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                Your website is your <em className="italic text-lak-sage">first impression.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={0.2}>
              <p className="text-lg text-lak-soft max-w-md">
                In the first three seconds, a visitor decides whether you are the kind of business they would trust with their money, or not.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-black/10">
          {items.map((it, i) => (
            <Reveal key={it.n} delay={i * 0.12}>
              <div className={`px-2 md:px-10 py-12 md:py-16 ${i < 2 ? "md:border-r border-black/10" : ""} border-b md:border-b-0 border-black/10`}>
                <span className="font-serif text-5xl italic text-lak-highlight">{it.n}</span>
                <h3 className="mt-8 font-serif text-3xl sm:text-4xl font-light tracking-tight">{it.title}</h3>
                <p className="mt-5 text-lak-soft leading-relaxed">{it.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Consequences() {
  const items = [
    { t: "Lost enquiries", b: "Every visitor who leaves is a quiet \"no\"." },
    { t: "Lower trust", b: "Credibility leaks from outdated pages." },
    { t: "Weak brand perception", b: "You're seen as a commodity, not a choice." },
    { t: "Fewer bookings", b: "Friction in design becomes friction in sales." },
    { t: "Missed opportunities", b: "Competitors quietly take what should have been yours." },
  ];
  return (
    <section data-testid={HOME.consequencesSection} className="lak-section py-28 sm:py-40 bg-lak-secondary">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <Reveal>
          <span className="eyebrow">The cost of doing nothing</span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02] max-w-4xl">
            What is your current website <em className="italic text-lak-sage">costing you?</em>
          </h2>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-black/10 border-y border-black/10">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={i * 0.08}>
              <div className="bg-lak-secondary p-8 lg:p-10 h-full min-h-[260px] flex flex-col justify-between">
                <span className="font-serif text-2xl italic text-lak-highlight">{String(i+1).padStart(2,"0")}</span>
                <div>
                  <h3 className="font-serif text-2xl lg:text-3xl font-light tracking-tight">{it.t}</h3>
                  <p className="mt-3 text-sm text-lak-soft">{it.b}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValueProp() {
  return (
    <section data-testid={HOME.valueSection} className="lak-section py-32 sm:py-48 bg-lak-bg">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-2">
            <Reveal><span className="eyebrow">Our belief</span></Reveal>
          </div>
          <div className="col-span-12 lg:col-span-10">
            <Reveal delay={0.05}>
              <p className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-light">
                Where independent brands meet the design language <em className="italic text-lak-sage">of premium houses.</em>
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-12 max-w-2xl text-lg text-lak-soft leading-relaxed">
                A website is the most considered first impression a brand will ever make. Each one is composed at the atelier with the same patience a flagship store receives, typography first, photography close behind, and a quiet attention to every detail.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyDifferent() {
  const items = [
    { k: "01", t: "One quiet studio. Not five agencies.", d: "Design, copy, build, hosting, and support, kept under one calm roof. One person who knows your project from first conversation to fifth refinement." },
    { k: "02", t: "Editorial design, quietly considered.", d: "The taste and patience of a flagship studio, brought to every project the atelier touches. Considered as the only thing on our desk." },
    { k: "03", t: "Online in under a week.", d: "Every site is ready in less than seven days. You are taking enquiries while competitors are still waiting on a quote." },
    { k: "04", t: "Mobile-first, always.", d: "Designed on the device most of your customers will actually use. Fast, light, and effortless on a phone before it is ever opened on a desktop." },
    { k: "05", t: "Built to convert, quietly.", d: "Every page has a job to do. Clear paths, refined copy, and the kind of trust signals that turn a glance into an enquiry." },
    { k: "06", t: "We stay with you.", d: "Hosting, security, refinements, and quiet advice as your business grows. Not a launch-and-leave studio." },
  ];
  return (
    <section className="lak-section py-28 sm:py-40 bg-lak-secondary">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-10 items-end mb-16">
          <div className="col-span-12 lg:col-span-7">
            <Reveal><span className="eyebrow">Why we are different</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                Not another agency. <em className="italic text-lak-sage">A quiet alternative.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={0.2}>
              <p className="text-lg text-lak-soft max-w-md">
                Six honest reasons businesses come to us instead of the usual agencies, freelancers, or off-the-shelf builders.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {items.map((it, i) => (
            <Reveal key={it.k} delay={i * 0.07}>
              <div className="bg-lak-secondary p-10 lg:p-12 h-full">
                <span className="font-serif italic text-lak-highlight text-3xl">{it.k}</span>
                <h3 className="mt-8 font-serif text-2xl lg:text-3xl font-light tracking-tight leading-snug">{it.t}</h3>
                <p className="mt-5 text-lak-soft leading-relaxed">{it.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


function Comparison() {
  const rows = [
    { label: "Website cost", agency: "£2,000 – £8,000+", lak: "Free" },
    { label: "Monthly fees", agency: "Hidden extras, hosting, plugins", lak: "£99, all in" },
    { label: "Turnaround", agency: "Six to eight weeks", lak: "Under a week" },
    { label: "Design approach", agency: "Templates, slight customisation", lak: "Bespoke, every detail" },
    { label: "Brand feel", agency: "Familiar, forgettable, generic", lak: "Made to feel like your brand" },
    { label: "Typography & palette", agency: "Preset styles and safe colours", lak: "Editorial type and brand-led colour" },
    { label: "Conversion thinking", agency: "Designed to decorate", lak: "Designed to build trust and convert" },
    { label: "Revisions", agency: "Limited rounds", lak: "Considered until right" },
    { label: "Direct studio access", agency: "Project manager, hand-offs", lak: "Direct line to the atelier" },
    { label: "Ongoing care", agency: "Out of scope after launch", lak: "Included in the retainer" },
  ];
  return (
    <section className="lak-section py-28 sm:py-40 bg-lak-bg">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-10 mb-14 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal><span className="eyebrow">The difference</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                Why not just use <em className="italic text-lak-sage">a typical agency?</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={0.2}>
              <p className="text-lg text-lak-soft max-w-md">
                The same boutique craft, without the agency invoice. A quiet, side-by-side look at how working with the atelier differs from the usual route.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Desktop / tablet table */}
        <Reveal>
          <div className="hidden md:block border-y border-black/10">
            <div className="grid grid-cols-12 py-5 border-b border-black/10">
              <div className="col-span-4 eyebrow">Item</div>
              <div className="col-span-4 eyebrow text-lak-soft">Typical agency</div>
              <div className="col-span-4 eyebrow text-lak-sage">LAK Atelier</div>
            </div>
            {rows.map((r, i) => (
              <div key={r.label} className={`grid grid-cols-12 items-center py-7 ${i < rows.length - 1 ? "border-b border-black/10" : ""}`}>
                <div className="col-span-4 font-serif italic text-lg text-lak-text">{r.label}</div>
                <div className="col-span-4 text-sm text-lak-soft line-through decoration-lak-soft/40">{r.agency}</div>
                <div className="col-span-4 font-serif italic text-xl text-lak-text">
                  <span className="inline-flex items-center gap-3">
                    <Check size={16} strokeWidth={1.5} className="text-lak-sage" />
                    {r.lak}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Mobile table */}
        <Reveal>
          <div className="md:hidden mt-4 overflow-hidden border border-black/10 bg-lak-bg text-left">
            <table className="w-full table-fixed border-collapse">
              <caption className="sr-only">Comparison between a typical agency and LAK Atelier</caption>
              {rows.map((r) => (
                <tbody key={r.label} className="border-b border-black/10 last:border-b-0">
                  <tr>
                    <th colSpan={2} scope="rowgroup" className="bg-lak-secondary px-5 py-3 eyebrow text-lak-text">
                      {r.label}
                    </th>
                  </tr>
                  <tr className="align-top">
                    <td className="w-1/2 border-r border-black/10 px-5 py-4">
                      <span className="eyebrow text-lak-soft">Agency</span>
                      <p className="mt-2 text-sm leading-relaxed text-lak-soft line-through decoration-lak-soft/40">
                        {r.agency}
                      </p>
                    </td>
                    <td className="w-1/2 px-5 py-4">
                      <span className="eyebrow text-lak-sage">LAK</span>
                      <p className="mt-2 font-serif italic text-base leading-snug text-lak-text">
                        {r.lak}
                      </p>
                    </td>
                  </tr>
                </tbody>
              ))}
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ProjectTypeCta() {
  return (
    <section className="lak-section py-28 sm:py-40 bg-lak-secondary border-y border-black/10">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 text-center">
        <Reveal><span className="eyebrow">Begin a project</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
            Tell us where you are.<br />
            <em className="italic text-lak-sage">We&rsquo;ll take it from there.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-xl mx-auto text-lg text-lak-soft">
            Do you already have a website, or are you launching one for the first time? Either way, the website itself is on the atelier.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 max-w-2xl mx-auto">
            <Link
              to="/contact?type=redesign"
              data-testid="home-cta-redesign"
              className="group flex-1 inline-flex items-center justify-between gap-4 px-6 py-5 btn-outline text-[11px] tracking-[0.28em] uppercase"
            >
              <span>I&rsquo;d like to redesign my current site</span>
              <ArrowUpRight size={16} strokeWidth={1.25} className="shrink-0" />
            </Link>
            <Link
              to="/contact?type=new"
              data-testid="home-cta-new"
              className="group flex-1 inline-flex items-center justify-between gap-4 px-6 py-5 btn-sage text-[11px] tracking-[0.28em] uppercase"
            >
              <span>I need a new website</span>
              <ArrowUpRight size={16} strokeWidth={1.25} className="shrink-0" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mt-10 eyebrow text-lak-soft">Replies within one working day &middot; UK based</p>
        </Reveal>
      </div>
    </section>
  );
}



function ShowcaseTeaser() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const desktopY = useTransform(scrollYProgress, [0, 1], [80, -120]);
  const mobileY = useTransform(scrollYProgress, [0, 1], [180, -260]);

  return (
    <section ref={ref} data-testid={HOME.showcaseSection} className="lak-section py-28 sm:py-40 bg-lak-secondary relative overflow-hidden">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-10 items-end mb-16">
          <div className="col-span-12 lg:col-span-7">
            <Reveal><span className="eyebrow">Featured Project &middot; 001</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-8xl font-light tracking-tight leading-[1.0]">
                Petal &amp; Paw<br />
                <em className="italic text-lak-sage">Pet-safe florals, beautifully online.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={0.2}>
              <p className="text-lg text-lak-soft max-w-md">
                A boutique pet-safe florist needed an online home as considered as their arrangements. We crafted an editorial e-commerce experience, gentle, confident, and unmistakably their own.
              </p>
              <Link to="/work/petal-and-paw" data-testid={HOME.showcaseCta} className="mt-10 inline-flex items-center gap-3 px-8 py-4 btn-outline text-[11px] tracking-[0.28em] uppercase">
                View The Case Study <ArrowUpRight size={16} strokeWidth={1.25} />
              </Link>
            </Reveal>
          </div>
        </div>

        <div className="lak-mobile-art-grid relative grid grid-cols-12 gap-6 items-start">
          <motion.div style={{ y: desktopY }} className="col-span-12 lg:col-span-9 relative">
            <div className="bg-lak-text/90 rounded-md p-2 sm:p-3 shadow-2xl">
              <div className="flex items-center gap-1.5 px-2 py-1.5">
                <span className="w-2 h-2 bg-white/20 rounded-full" />
                <span className="w-2 h-2 bg-white/20 rounded-full" />
                <span className="w-2 h-2 bg-white/20 rounded-full" />
                <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-white/40">petalandpaw.co.uk</span>
              </div>
              <div className="overflow-hidden zoom-frame">
                <img src="/petalpaw-desktop-1.jpg" alt="Petal & Paw, desktop" className="w-full block" />
              </div>
            </div>
          </motion.div>

          <motion.div style={{ y: mobileY }} className="col-span-7 sm:col-span-5 lg:col-span-3 lg:-ml-16 lg:mt-16 relative z-10 mx-auto">
            <div className="bg-lak-text/90 rounded-[28px] p-2 shadow-2xl border border-white/10">
              <div className="rounded-[22px] overflow-hidden aspect-[9/19]">
                <img src="/petalpaw-mobile-1.png" alt="Petal & Paw, mobile" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mt-20 border-y border-black/10">
          {[
            { k: "Brand", v: "Petal & Paw" },
            { k: "Industry", v: "Pet-safe Florals" },
            { k: "Challenge", v: "Stand apart in a busy market" },
            { k: "Outcome", v: "An editorial e-commerce home" },
          ].map((s) => (
            <div key={s.k} className="bg-lak-secondary p-8">
              <span className="eyebrow">{s.k}</span>
              <p className="mt-3 font-serif text-2xl italic font-light">{s.v}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function Testimonials() {
  return (
    <section data-testid={HOME.testimonialsSection} className="lak-section py-28 sm:py-40 bg-lak-secondary relative">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <Reveal><span className="eyebrow">Testimonial</span></Reveal>
        <div className="grid grid-cols-12 gap-10 mt-10 items-center">
          <div className="col-span-12 lg:col-span-8">
            <Reveal delay={0.1}>
              <Quote size={36} strokeWidth={1} className="text-lak-highlight" />
              <p className="mt-8 font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] tracking-tight">
                &ldquo;LAK Atelier built our boutique a beautiful website in less than a week.
                Calm, considered, and unmistakably ours from the very start.&rdquo;
              </p>
              <div className="mt-10 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-lak-highlight/30 flex items-center justify-center font-serif italic text-lak-sage">P</div>
                <div>
                  <p className="font-serif italic text-lg">Owner, Petal &amp; Paw</p>
                  <p className="eyebrow text-lak-soft">London, UK</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-6 lg:border-l border-black/10">
            <Reveal delay={0.2}>
              <span className="eyebrow">In their words</span>
              <p className="mt-5 font-serif text-2xl italic font-light leading-snug text-lak-soft">
                A first chapter, written with care.
              </p>
              <div className="mt-8 space-y-4 text-sm text-lak-soft">
                <p><span className="eyebrow block mb-1">Built for</span>An independent boutique launching online.</p>
                <p><span className="eyebrow block mb-1">Designed around</span>Brand, story, and a calm, considered checkout.</p>
                <p><span className="eyebrow block mb-1">Outcome</span>An online home that finally feels like them.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section data-testid={HOME.finalCta} className="lak-section py-32 sm:py-48 bg-lak-bg relative overflow-hidden">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 text-center">
        <Reveal><span className="eyebrow">A new chapter for your business</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-9xl font-light tracking-tight leading-[0.95]">
            Designed to feel<br />
            <em className="italic text-lak-sage">like your brand.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/contact" className="mt-14 inline-flex items-center gap-3 px-10 py-5 btn-sage text-[11px] tracking-[0.28em] uppercase">
            Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function StudioNavigator() {
  const cards = [
    {
      n: "01",
      eyebrow: "Why us",
      title: "Services",
      copy: "Six considered reasons businesses choose, and stay with, the atelier.",
      to: "/services",
      cta: "View Services",
    },
    {
      n: "02",
      eyebrow: "The offer",
      title: "Pricing",
      copy: "Your website built free. £99 a month covers hosting, support and ongoing SEO. No contracts.",
      to: "/pricing",
      cta: "View Pricing",
    },
    {
      n: "03",
      eyebrow: "How we work",
      title: "Process",
      copy: "Five quiet, considered stages, from first conversation to launch and beyond.",
      to: "/process",
      cta: "View Process",
    },
  ];
  return (
    <section className="lak-section py-28 sm:py-40 bg-lak-bg">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-10 items-end mb-16">
          <div className="col-span-12 lg:col-span-7">
            <Reveal><span className="eyebrow">More from the atelier</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                Take a quiet look <em className="italic text-lak-sage">at the studio.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={0.2}>
              <p className="text-lg text-lak-soft max-w-md">
                Three short pages, what we offer, what it costs, and how the work actually unfolds. Read at your own pace.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <Link to={c.to} className="block bg-lak-bg p-10 lg:p-12 h-full group transition-colors hover:bg-lak-secondary">
                <div className="flex items-start justify-between">
                  <span className="font-serif italic text-lak-highlight text-3xl">{c.n}</span>
                  <ArrowUpRight size={22} strokeWidth={1.25} className="text-lak-soft group-hover:text-lak-sage transition-colors" />
                </div>
                <span className="mt-10 eyebrow block">{c.eyebrow}</span>
                <h3 className="mt-4 font-serif text-4xl lg:text-5xl font-light tracking-tight">{c.title}</h3>
                <p className="mt-6 text-lak-soft leading-relaxed" dangerouslySetInnerHTML={{ __html: c.copy }} />
                <span className="mt-10 inline-block eyebrow link-underline">{c.cta}</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Seo
        title="Boutique Web Design · Built Free, £99/month Retainer"
        description="LAK Atelier builds bespoke editorial websites for independent UK brands. Your website is free. A simple £99 monthly retainer covers hosting, support and ongoing SEO."
        keywords="boutique web design, luxury web design, website design for small businesses, creative business websites, lifestyle brand websites, London web designer"
        schemas={[offerSchema()]}
      />
      <Hero />
      <Marquee />
      <Problem />
      <Consequences />
      <ValueProp />
      <WhyDifferent />
      <Comparison />
      <ShowcaseTeaser />
      <ProjectTypeCta />
      <StudioNavigator />
      <Testimonials />
      <FinalCta />
    </main>
  );
}
